import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Smartphone, Copy, Check, Apple, Chrome } from "lucide-react";
import {
  getMobileInstallUrl,
  useQrDataUrl,
} from "@/hooks/use-pwa-install";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function PwaInstallDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const url = getMobileInstallUrl();
  const qr = useQrDataUrl(url, 192, open);
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast({ title: t("seg10.linkCopied"), description: url });
    } catch {
      toast({
        title: t("seg10.copyFailed"),
        description: t("seg10.copyFailedDesc"),
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[520px] p-0 overflow-hidden"
        data-testid="dialog-pwa-install"
      >
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
              <Smartphone className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <DialogHeader className="space-y-0">
                <DialogTitle className="text-white text-lg">
                  {t("seg10.pwaTitle")}
                </DialogTitle>
              </DialogHeader>
              <DialogDescription className="text-slate-300 text-[12px] mt-0.5">
                {t("seg10.pwaDescription")}
              </DialogDescription>
            </div>
          </div>
        </div>

        <div className="p-6 grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-6 items-start">
          <div className="mx-auto sm:mx-0">
            <div className="w-[192px] h-[192px] rounded-xl border border-slate-200 bg-white p-2 flex items-center justify-center">
              {qr ? (
                <img
                  src={qr}
                  alt={t("seg10.qrAlt", { url })}
                  className="w-full h-full"
                  data-testid="img-pwa-qr"
                />
              ) : (
                <div className="w-full h-full bg-slate-100 animate-pulse rounded-md" />
              )}
            </div>
            <p className="text-[11px] text-slate-500 text-center mt-2">
              {t("seg10.scanWithCamera")}
            </p>
          </div>

          <div className="space-y-4 min-w-0">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                {t("seg10.openLinkOnPhone")}
              </p>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={url}
                  onFocus={(e) => e.currentTarget.select()}
                  className="font-mono text-[12px] h-9"
                  data-testid="input-pwa-install-url"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9 px-3 shrink-0"
                  onClick={copyLink}
                  data-testid="button-pwa-copy-link"
                >
                  {copied ? (
                    <Check className="w-3.5 h-3.5" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                  <span className="ms-1.5 text-[12px]">
                    {copied ? t("seg10.copied") : t("seg10.copy")}
                  </span>
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-start gap-2.5 p-2.5 rounded-lg bg-slate-50 border border-slate-200">
                <Chrome className="w-4 h-4 text-slate-700 mt-0.5 shrink-0" />
                <div>
                  <p className="text-[12px] font-semibold text-slate-900">
                    Android
                  </p>
                  <p className="text-[11px] text-slate-600">
                    {t("seg10.androidText")}{" "}
                    <span className="font-medium">{t("seg10.installApp")}</span>.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2.5 p-2.5 rounded-lg bg-slate-50 border border-slate-200">
                <Apple className="w-4 h-4 text-slate-700 mt-0.5 shrink-0" />
                <div>
                  <p className="text-[12px] font-semibold text-slate-900">
                    iPhone / iPad
                  </p>
                  <p className="text-[11px] text-slate-600">
                    {t("seg10.iosText")}{" "}
                    <span className="font-medium">{t("seg10.addToHomeScreen")}</span>
                    .
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
